import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { currencySymbol } from "@/lib/constants";
import CustomerEditDialog from "@/components/CustomerEditDialog";

export default function CustomersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const deleteCustomer = async (c) => {
    const first = window.confirm(
      `Delete customer "${c.name}${c.company ? ` — ${c.company}` : ""}"?\n\n` +
      `Their login will be removed. If they still have jobs, this will fail and you'll be asked whether to cascade.`
    );
    if (!first) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success(`Customer "${c.name}" deleted`);
      load();
      return;
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiError(err.response?.data?.detail) || "Delete failed";
      if (status !== 409) {
        toast.error(detail);
        return;
      }
      // Confirm cascade
      const forceOk = window.confirm(
        `${detail}\n\nCascade delete — remove this customer AND all their jobs, invoices, rate confirmations, and POD files? This cannot be undone.`
      );
      if (!forceOk) return;
      try {
        const { data } = await api.delete(`/customers/${c.id}?force=true`);
        toast.success(`Customer "${c.name}" deleted (removed ${data?.jobs_removed || 0} job(s) and related documents)`);
        load();
      } catch (err2) {
        toast.error(formatApiError(err2.response?.data?.detail) || "Cascade delete failed");
      }
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="mono-label">ROSTER</div>
          <h2 className="text-2xl font-bold tracking-tight mt-1">Customers</h2>
          <p className="text-sm text-zinc-600">Shippers registered via the portal or added by staff. Billing email is used when sending invoices.</p>
        </div>
        <Button
          onClick={() => { setEditing(null); setOpen(true); }}
          className="sharp bg-[#0056B3] hover:bg-[#004494]"
          data-testid="customers-new-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> New customer
        </Button>
      </div>
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="apex-grid">
          <thead>
            <tr>
              <th className="text-left py-3 px-4">Name</th>
              <th className="text-left py-3 px-4">Company</th>
              <th className="text-left py-3 px-4">Email</th>
              <th className="text-left py-3 px-4">Credit limit</th>
              <th className="text-left py-3 px-4">Outstanding</th>
              <th className="text-left py-3 px-4">Utilization</th>
              <th className="text-left py-3 px-4">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-8 text-center mono-label">Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center mono-label">No customers yet</td></tr>
            ) : items.map((c, i) => {
              const sym = currencySymbol(c.credit_currency);
              const isActive = c.is_active !== false;
              const over = c.credit_over_limit;
              return (
                <tr key={c.id} className={`${i % 2 ? "bg-zinc-50" : ""} ${!isActive ? "opacity-60" : ""}`} data-testid={`customer-row-${c.id}`}>
                  <td className="py-3 px-4 font-medium">{c.name}</td>
                  <td className="py-3 px-4">{c.company || "—"}</td>
                  <td className="py-3 px-4 font-mono text-xs">{c.email}</td>
                  <td className="py-3 px-4 font-mono">
                    {c.credit_limit ? `${sym}${c.credit_limit.toLocaleString()} ${c.credit_currency}` : <span className="text-zinc-400">Unlimited</span>}
                  </td>
                  <td className="py-3 px-4 font-mono">
                    {sym}{(c.outstanding_total || 0).toLocaleString()}
                    {c.outstanding_count > 0 && <span className="text-[10px] text-zinc-500 ml-1">({c.outstanding_count})</span>}
                  </td>
                  <td className="py-3 px-4 w-32">
                    {c.credit_utilization_pct != null ? (
                      <div>
                        <div className="mono-label !text-[10px]">{c.credit_utilization_pct}%</div>
                        <div className="mt-1 h-1.5 bg-zinc-200 relative">
                          <div
                            className={`absolute inset-y-0 left-0 ${over ? "bg-rose-600" : c.credit_utilization_pct > 80 ? "bg-amber-500" : "bg-emerald-600"}`}
                            style={{ width: `${Math.min(100, c.credit_utilization_pct)}%` }}
                          />
                        </div>
                      </div>
                    ) : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="py-3 px-4">
                    {!isActive ? (
                      <span className="inline-flex px-2 py-0.5 border font-mono text-[10px] uppercase bg-zinc-900 text-white border-zinc-900">INACTIVE</span>
                    ) : over ? (
                      <span className="inline-flex px-2 py-0.5 border font-mono text-[10px] uppercase bg-rose-100 text-rose-900 border-rose-300">OVER LIMIT</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 border font-mono text-[10px] uppercase bg-emerald-100 text-emerald-900 border-emerald-300">ACTIVE</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button onClick={() => { setEditing(c); setOpen(true); }}
                        className="text-[#0056B3] inline-flex items-center text-xs" data-testid={`cust-edit-${c.id}`}>
                        <Pencil className="h-3 w-3 mr-1" /> Edit
                      </button>
                      <button onClick={() => deleteCustomer(c)}
                        className="text-rose-700 inline-flex items-center text-xs hover:text-rose-900" data-testid={`cust-delete-${c.id}`}>
                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <CustomerEditDialog open={open} onOpenChange={setOpen} customer={editing} onSaved={load} />
    </div>
  );
}
